import React, { FC, useState } from 'react';
import { Dropdown, Menu, Typography, Modal, Button, Space } from 'antd';
import { useTranslation } from 'react-i18next';
import { EllipsisOutlined, ExclamationCircleOutlined } from '@ant-design/icons';

import { Beneficiary, BeneficiaryAccount } from '~/types';
import { EditIcon, RemoveIcon } from '~/img';
import SendMoney from './SendMoney';
import { usePermissions } from '~/hooks';
import { useBeneficiaryAccountsDelete } from '~/hooks/beneficiaryAccounts';
import EditAccount from './EditAccount';

interface Props {
  beneficiary: Beneficiary;
  beneficiaryAccount: BeneficiaryAccount;
}

const AccountActions: FC<Props> = (props) => {
  const { beneficiary, beneficiaryAccount } = props;
  const [t] = useTranslation('payouts');
  const dt = (key: string): string => t(`translation:${key}`);
  const [editOpen, setEditOpen] = useState(false);
  const handleEditOpen = () => {
    setEditOpen(true);
  };
  const handleEditClose = () => {
    setEditOpen(false);
  };

  const { mutate } = useBeneficiaryAccountsDelete();
  const deleteBeneficiaryAccounts = () => {
    mutate({
      beneficiaryId: beneficiaryAccount.beneficiaryId,
      id: beneficiaryAccount.id,
    });
  };

  const handleDelete = () => {
    if (beneficiaryAccount.primary) {
      Modal.confirm({
        title: t('sureDelete'),
        content: t('deletePrimaryAccount'),
        icon: <ExclamationCircleOutlined />,
        okText: dt('yes'),
        okType: 'danger',
        cancelText: dt('no'),
        onOk: deleteBeneficiaryAccounts,
      });
    } else {
      Modal.confirm({
        title: t('sureDelete'),
        icon: <ExclamationCircleOutlined />,
        content: t('deleteDescription'),
        okText: dt('yes'),
        okType: 'danger',
        cancelText: dt('no'),
        onOk: deleteBeneficiaryAccounts,
      });
    }
  };

  const hasAccessUpdate =
    usePermissions('business.beneficiary.update') && beneficiaryAccount.status !== 'PENDING';
  const hasAccessSendMoney = usePermissions('business.beneficiary.payout');

  return (
    <>
      <EditAccount
        open={editOpen}
        onClose={handleEditClose}
        beneficiary={beneficiary}
        beneficiaryAccount={beneficiaryAccount}
      />
      <Space size={4}>
        {hasAccessSendMoney && (
          <SendMoney beneficiaryAccount={beneficiaryAccount} beneficiary={beneficiary} />
        )}
        {hasAccessUpdate && (
          <Dropdown
            trigger={['click']}
            overlay={
              <Menu>
                <Menu.Item key="delete" onClick={handleEditOpen}>
                  <Typography.Text>
                    <Space size={4}>
                      <EditIcon />
                      {dt('edit')}
                    </Space>
                  </Typography.Text>
                </Menu.Item>
                <Menu.Item key="delete" onClick={handleDelete}>
                  <Typography.Text type="danger">
                    <Space size={4}>
                      <RemoveIcon />
                      {dt('delete')}
                    </Space>
                  </Typography.Text>
                </Menu.Item>
              </Menu>
            }
          >
            <Button icon={<EllipsisOutlined />} className="PayoutsCollapse-OptionsButton" />
          </Dropdown>
        )}
      </Space>
    </>
  );
};

export default AccountActions;
